import * as React from 'react'
import { FieldErrorMessage, FieldHelper, Input } from '../primitives'
import { useStableId } from '../hooks/useStableId'
import { faClockSolid, FaIcon } from '@/lib/fa'



const formatToHHMM = (value?: string | null) => {
  if (!value) return ''
  if (/^\d{2}:\d{2}$/.test(value)) return value
  return value
}

const parseHHMM = (input: string): string | null => {
  const trimmed = input.trim()
  const match = trimmed.match(/^(\d{1,2}):(\d{1,2})$/)
  if (!match) return null

  const hours = parseInt(match[1], 10)
  const minutes = parseInt(match[2], 10)

  if (hours < 0 || hours > 23) return null
  if (minutes < 0 || minutes > 59) return null

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

type TimeFieldProps = React.ComponentProps<typeof Input> & {
  setValue: (time: string | null) => void
  label?: string
  placeholder?: string
  helper?: React.ReactNode
  error?: string | null
  inputSize?: 'sm' | 'md'
}

export const TimeField: React.FC<TimeFieldProps> = ({
  setValue,
  label,
  value,
  error,
  disabled,
  id,
  required,
  helper,
  placeholder = 'HH:MM',
  inputSize = 'md',
  ...inputProps
}) => {
  const autoId = useStableId()
  const inputId = id ?? `autoid-${autoId}`
  const helperId = helper ? `${inputId}-helper` : undefined
  const errorId = error ? `${inputId}-error` : undefined

  const describedBy = [helperId, errorId].filter(Boolean).join(' ') || undefined

  const containerRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)

  const [inputValue, setInputValue] = React.useState('')
  const cursorPosRef = React.useRef<number | null>(null)

  React.useEffect(() => {
    setInputValue(formatToHHMM(value))
  }, [value])

  // Restore cursor position after React re-render
  React.useEffect(() => {
    if (cursorPosRef.current !== null && inputRef.current) {
      const pos = cursorPosRef.current
      inputRef.current.setSelectionRange(pos, pos)
      cursorPosRef.current = null
    }
  })

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target
    const raw = input.value
    const cursorPos = input.selectionStart ?? 0

    let cleaned = raw.replace(/[^\d:]/g, '')
    const firstColon = cleaned.indexOf(':')
    if (firstColon !== -1) {
      cleaned =
        cleaned.slice(0, firstColon + 1) +
        cleaned.slice(firstColon + 1).replace(/:/g, '')
    }

    let hh = ''
    let mm = ''
    let hasColon = cleaned.includes(':')

    if (hasColon) {
      const [h, m = ''] = cleaned.split(':')
      hh = h.slice(0, 2)
      mm = m.slice(0, 2)
    } else if (cleaned.length <= 2) {
      hh = cleaned
    } else {
      hh = cleaned.slice(0, 2)
      mm = cleaned.slice(2, 4)
      hasColon = true
    }

    const formatted = hasColon ? `${hh}:${mm}` : hh

    setInputValue(formatted)

    const diff = formatted.length - raw.length
    cursorPosRef.current = Math.max(0, cursorPos + diff)
  }

  const handleInputBlur = () => {
    const parsed = parseHHMM(inputValue)

    if (parsed) {
      setValue(parsed)
      setInputValue(parsed)
    } else if (inputValue === '') {
      setValue(null)
    } else {
      setInputValue(formatToHHMM(value))
    }
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setValue(null)
    setInputValue('')
  }

  return (
    <div
      ref={containerRef}
      className={`tw-relative tw-z-50 tw-inline-block ${inputSize === 'sm' ? '' : 'tw-w-full'}`}
    >
      {label && (
        <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium tw-text-gray-700">
          {required && <span className="tw-mr-0.5 tw-text-error">*</span>}
          {label}
        </label>
      )}

      <div
        className={`tw-relative tw-flex tw-items-center ${inputSize === 'sm' ? 'tw-max-w-[74px]' : 'tw-max-w-none'}`}
      >
        <Input
          ref={inputRef}
          disabled={disabled}
          aria-describedby={describedBy}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          required={required}
          placeholder={placeholder}
          {...inputProps}
          className={`tw-block tw-w-full tw-rounded-md tw-border tw-border-gray-300 tw-bg-white  tw-leading-tight tw-placeholder-gray-400 tw-shadow-sm focus:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 disabled:tw-cursor-not-allowed disabled:tw-bg-gray-100 disabled:tw-text-gray-400 data-[invalid=true]:tw-border-red-500 data-[invalid=true]:focus:tw-ring-red-500 ${inputSize === 'sm' ? 'tw-px-2 tw-py-1 tw-text-xs' : 'tw-px-3 tw-py-2 tw-text-sm'} `}
        />
        <div
          className={`tw-absolute tw-flex tw-items-center tw-gap-2 ${inputSize === 'sm' ? 'tw-right-2' : 'tw-right-3'}`}
        >
          <FaIcon
            icon={faClockSolid}
            className={`tw-text-content-base ${inputSize === 'sm' ? 'tw-h-3 tw-w-3' : 'tw-h-4 tw-w-4'}`}
          />
        </div>
      </div>

      {helper && (
        <FieldHelper
          id={helperId}
          className="tw-mt-0.5 tw-text-xs tw-text-gray-500"
        >
          {helper}
        </FieldHelper>
      )}

      {error && (
        <FieldErrorMessage
          id={errorId}
          className="tw-mt-0.5 tw-text-xs tw-font-medium tw-text-red-600"
        >
          {error}
        </FieldErrorMessage>
      )}
    </div>
  )
}
